import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { 
  MapPin, Bed, Bath, Square, ArrowLeft, ChevronLeft, ChevronRight,
  MessageCircle, Calendar, Loader2, Home, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ListingDetail = () => {
  const { slug } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showGallery, setShowGallery] = useState(false);

  useEffect(() => {
    fetchListing();
  }, [slug]);

  const fetchListing = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/listings/${slug}`);
      if (!response.ok) {
        if (response.status === 404) {
          setError('Listing not found');
        } else {
          throw new Error('Failed to fetch listing');
        }
        return;
      }
      const data = await response.json();
      setListing(data);
    } catch (err) {
      setError('Unable to load listing');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (amount, currency, intentType) => {
    if (!amount) return '';
    const formatted = `${currency === 'MXN' ? 'MX$' : '$'}${amount.toLocaleString()}`;
    return intentType === 'rent' ? `${formatted}/month` : formatted;
  };

  const nextImage = () => {
    if (listing?.images?.length) {
      setCurrentImageIndex((prev) => (prev + 1) % listing.images.length);
    }
  };

  const prevImage = () => {
    if (listing?.images?.length) {
      setCurrentImageIndex((prev) => (prev - 1 + listing.images.length) % listing.images.length);
    }
  };

  const getIntakeUrl = () => {
    if (!listing) return '/relocation/blueprint';
    const params = new URLSearchParams();
    params.set('intent', listing.intent);
    params.set('city', listing.city);
    if (listing.neighborhood) params.set('neighborhood', listing.neighborhood);
    return `/relocation/blueprint?${params.toString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6EF] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9F6EF]">
        <Header />
        <div className="pt-32 pb-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Home className="w-16 h-16 text-[#333] mx-auto mb-6" />
            <h1 className="text-2xl text-white mb-4">{error}</h1>
            <Link
              to="/relocation/listings"
              className="inline-flex items-center gap-2 text-[#2CA8A0] hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to listings
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      <Header />

      {/* Fullscreen Gallery Modal */}
      {showGallery && listing?.images?.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
          <button
            onClick={() => setShowGallery(false)}
            className="absolute top-4 right-4 w-10 h-10 bg-[#FFFFFF] rounded-full flex items-center justify-center text-white hover:bg-[#333] transition z-10"
          >
            <X className="w-5 h-5" />
          </button>
          
          {listing.images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-4 w-12 h-12 bg-[#FFFFFF] rounded-full flex items-center justify-center text-white hover:bg-[#333] transition"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 w-12 h-12 bg-[#FFFFFF] rounded-full flex items-center justify-center text-white hover:bg-[#333] transition"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          
          <img
            src={`${API_URL}${listing.images[currentImageIndex].url}`}
            alt=""
            className="max-w-full max-h-full object-contain"
          />
          
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
            {currentImageIndex + 1} / {listing.images.length}
          </div>
        </div>
      )}

      {/* Back Link */}
      <div className="pt-24 pb-4">
        <div className="max-w-6xl mx-auto px-6">
          <Link
            to="/relocation/listings"
            className="inline-flex items-center gap-2 text-[#888] hover:text-white transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to listings
          </Link>
        </div>
      </div>

      {/* Gallery */}
      <section className="pb-12">
        <div className="max-w-6xl mx-auto px-6">
          {listing?.images?.length > 0 ? (
            <div className="relative">
              {/* Main Image */}
              <div 
                className="aspect-[16/9] bg-[#111] rounded-lg overflow-hidden cursor-pointer"
                onClick={() => setShowGallery(true)}
              >
                <img
                  src={`${API_URL}${listing.images[currentImageIndex].url}`}
                  alt={listing.title}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Navigation */}
              {listing.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  
                  {/* Thumbnails */}
                  <div className="flex justify-center gap-2 mt-4">
                    {listing.images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`w-16 h-12 rounded overflow-hidden border-2 transition ${
                          idx === currentImageIndex 
                            ? 'border-[#2CA8A0]' 
                            : 'border-transparent opacity-60 hover:opacity-100'
                        }`}
                      >
                        <img
                          src={`${API_URL}${img.url}`}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="aspect-[16/9] bg-[#111] rounded-lg flex items-center justify-center">
              <Home className="w-16 h-16 text-[#333]" />
            </div>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Header */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-3">
                  <span className={`px-2.5 py-1 text-xs font-medium ${
                    listing.intent === 'rent'
                      ? 'bg-blue-500 text-white'
                      : 'bg-green-500 text-white'
                  }`}>
                    {listing.intent === 'rent' ? 'For Rent' : 'For Sale'}
                  </span>
                  {listing.tags?.map((tag, idx) => (
                    <span key={idx} className="px-2.5 py-1 text-xs bg-[#FFFFFF] text-[#888]">
                      {tag}
                    </span>
                  ))}
                </div>
                
                <h1 className="text-3xl lg:text-4xl font-light text-white mb-3" data-testid="listing-title">
                  {listing.title}
                </h1>
                
                {(listing.neighborhood || listing.city) && (
                  <div className="flex items-center gap-2 text-[#888]">
                    <MapPin className="w-4 h-4" />
                    <span>{listing.neighborhood ? `${listing.neighborhood}, ` : ''}{listing.city}</span>
                  </div>
                )}
              </div>

              {/* Price & Facts */}
              <div className="flex flex-wrap items-center gap-6 mb-8 pb-8 border-b border-[#FFFFFF]">
                <div>
                  <p className="text-3xl text-[#2CA8A0] font-medium">
                    {formatPrice(listing.price_amount, listing.price_currency, listing.intent)}
                  </p>
                </div>
                
                {listing.bedrooms && (
                  <div className="flex items-center gap-2 text-[#888]">
                    <Bed className="w-5 h-5" />
                    <span>{listing.bedrooms} {listing.bedrooms === 1 ? 'Bedroom' : 'Bedrooms'}</span>
                  </div>
                )}
                {listing.bathrooms && (
                  <div className="flex items-center gap-2 text-[#888]">
                    <Bath className="w-5 h-5" />
                    <span>{listing.bathrooms} {listing.bathrooms === 1 ? 'Bathroom' : 'Bathrooms'}</span>
                  </div>
                )}
                {listing.area_sqm && (
                  <div className="flex items-center gap-2 text-[#888]">
                    <Square className="w-5 h-5" />
                    <span>{listing.area_sqm} m²</span>
                  </div>
                )}
              </div>

              {/* Description */}
              {listing.description && (
                <div className="mb-8">
                  <h2 className="text-sm text-[#666] uppercase tracking-wider mb-4">Description</h2>
                  <div className="text-[#1A5C6E] leading-relaxed whitespace-pre-wrap">
                    {listing.description}
                  </div>
                </div>
              )}
            </div>

            {/* CTA Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-[#F9F6EF] border border-[#FFFFFF] p-6">
                <h3 className="text-lg text-white font-medium mb-2">Interested in this property?</h3>
                <p className="text-sm text-[#888] mb-6">
                  Submit an advisory request and we'll help coordinate a viewing or introduction.
                </p>

                <Link
                  to={getIntakeUrl()}
                  className="flex items-center justify-center gap-2 w-full bg-[#2CA8A0] text-black py-3.5 font-medium hover:bg-[#2CA8A0] transition mb-3"
                  data-testid="request-intro-btn"
                >
                  <Calendar className="w-4 h-4" />
                  Request Intro / Schedule Viewing
                </Link>

                <a
                  href="https://wa.me/525554567678"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full border border-[#333] text-white py-3.5 hover:bg-white hover:text-black transition"
                  data-testid="whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>

                <p className="text-xs text-[#666] mt-6 text-center">
                  All viewings are coordinated through our advisory team.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ListingDetail;
